import { isTouchCapableMac } from "./browserDetection";
import { detectLaunchContext } from "./launchContext";

/**
 * LSP-21826: shared logic for the "Open / Get the LiveSwitch app" banner. contact-admin shows it on
 * the dashboard home page; contact-async shows it before a dashboard-launched recording. Both render
 * the shared MobileAppBanner.vue and own only the app-specific parts (which account, which API
 * client, which telemetry SDK copy).
 *
 * What each platform can tell us:
 *  - Android Chrome: `navigator.getInstalledRelatedApps()` reports whether our Play package is
 *    installed, provided the page's web manifest lists it under `related_applications` and the
 *    Android app declares this origin in its `asset_statements`. Installed → OPEN, otherwise → GET.
 *  - iOS: no web API exposes whether an app is installed, so the label is always GET. The App Store
 *    page itself shows Open when the app is already present.
 *
 * Dismissals live on the ACCOUNT (contact-api helpers/mobileAppBanner.ts owns the ladder: 3 days, 7 days,
 * 30 days, then opted out at 4 dismissals). Tapping OPEN or GET never snoozes; it is only counted in
 * Datadog. The server copy is the only one:
 * both hosts read the account from /me before the banner can render, so a browser-side copy would add
 * nothing, and it would keep the banner hidden after the server's snooze was cleared or shortened.
 */

export const MOBILE_APP_STORE_URL = "https://apps.apple.com/us/app/liveswitch/id6761065250";
export const MOBILE_GOOGLE_PLAY_URL = "https://play.google.com/store/apps/details?id=com.liveswitch.mobile";

/**
 * Android package name. Must match `package_name` in contact-admin's public/.well-known/assetlinks.json
 * and the `id` under `related_applications` in every web manifest that hosts this banner, or Chrome's
 * installed-app query answers nothing for our own app.
 */
export const MOBILE_ANDROID_PACKAGE_NAME = "com.liveswitch.mobile";

/**
 * The app's Uploads route (LSP-19393). OPEN hands it to the app as `liveswitch://uploads?organizationId=<id>`
 * through an Android intent URL, never as the https `/mobile/uploads` App Link: Chrome keeps a tap on a link to
 * the page's own site instead of handing it to the app, and the app sends an https `/mobile/uploads` without an
 * organizationId back to the browser, which shows the stuck-uploads recovery page (found on a Pixel, Android 13).
 * The app only accepts this route with exactly one organizationId (parseUploadsRoute in contact-mobile).
 */
export const MOBILE_APP_OPEN_ROUTE = "uploads";

/** Mirror of MOBILE_APP_BANNER_OPT_OUT_DISMISSALS in contact-api helpers/mobileAppBanner.ts. */
export const MOBILE_APP_BANNER_OPT_OUT_DISMISSALS = 4;

export type MobileAppPlatform = "ios" | "android" | "other";
export type MobileAppInstallState = "installed" | "not-installed" | "unknown";
export type MobileAppBannerLabel = "Open" | "Get";
/** What the hosts report to the API. Only the × snoozes, so a tap is telemetry only. */
export type MobileAppBannerEvent = "dismissed";

/** The slice of `/me` `account` the banner reads. */
export interface MobileAppBannerAccount {
	id: string;
	/** Which organization OPEN asks the app to show Uploads for. */
	organizationId?: string | null;
	mobileAppBannerDismissCount?: number | null;
	mobileAppBannerSnoozedUntil?: string | null;
}

/** What the shared component emits with every shown / tapped / dismissed event. */
export interface MobileAppBannerTelemetry {
	label: MobileAppBannerLabel;
	platform: MobileAppPlatform;
	installState: MobileAppInstallState;
}

interface RelatedApplication {
	platform?: string;
	id?: string;
	url?: string;
}

type NavigatorWithRelatedApps = Navigator & {
	getInstalledRelatedApps?: () => Promise<RelatedApplication[]>;
};

export function detectMobileAppPlatform(
	userAgent: string | undefined,
	navigatorLike?: Pick<Navigator, "platform" | "maxTouchPoints">
): MobileAppPlatform {
	const agent = userAgent ?? "";
	// iPadOS desktop mode identifies as a touch-capable Mac, using the shared browser detector.
	if (/iPhone|iPod|iPad/i.test(agent) || (navigatorLike && isTouchCapableMac(navigatorLike))) return "ios";
	if (/Android/i.test(agent)) return "android";
	return "other";
}

/**
 * How long the banner waits for Chrome's installed-app answer before rendering GET anyway. The banner
 * stays hidden until the answer (or this cap) arrives, so it never shows GET and then flips to OPEN.
 */
export const MOBILE_APP_INSTALL_QUERY_TIMEOUT_MS = 1500;

export async function queryMobileAppInstallState(
	platform: MobileAppPlatform,
	navigatorLike: Partial<NavigatorWithRelatedApps> | undefined,
	timeoutMs: number = MOBILE_APP_INSTALL_QUERY_TIMEOUT_MS
): Promise<MobileAppInstallState> {
	if (platform !== "android") return "unknown";
	const query = navigatorLike?.getInstalledRelatedApps;
	if (typeof query !== "function") return "unknown";
	let timer: ReturnType<typeof setTimeout> | undefined;
	try {
		const timedOut = new Promise<"timeout">((resolve) => {
			timer = setTimeout(() => resolve("timeout"), timeoutMs);
		});
		const related = await Promise.race([query.call(navigatorLike), timedOut]);
		if (related === "timeout") return "unknown";
		if (!Array.isArray(related)) return "unknown";
		return related.some((app) => app?.platform === "play" && app?.id === MOBILE_ANDROID_PACKAGE_NAME) ? "installed" : "not-installed";
	} catch {
		// Chrome rejects when the manifest has no related_applications or the asset statement is
		// missing. Either way we do not know, and GET is the safe label.
		return "unknown";
	} finally {
		if (timer !== undefined) clearTimeout(timer);
	}
}

export function resolveMobileAppBannerLabel(installState: MobileAppInstallState): MobileAppBannerLabel {
	return installState === "installed" ? "Open" : "Get";
}

/**
 * OPEN launches the installed Android app on Uploads; GET goes to the platform's store. Only Android can
 * report "installed", so OPEN is always an Android intent URL. Chrome hands an intent URL to the named
 * package even from a page on the same site, and opens Google Play instead if the package is missing.
 */
export function resolveMobileAppBannerHref(
	platform: MobileAppPlatform,
	installState: MobileAppInstallState,
	organizationId: unknown
): string {
	const storeUrl = platform === "ios" ? MOBILE_APP_STORE_URL : MOBILE_GOOGLE_PLAY_URL;
	if (platform !== "android" || installState !== "installed") return storeUrl;
	// An organizationId the app would refuse, or one that would break the intent URL (`;`, `#`, `/`, `%`), is
	// left off. A bare `uploads` route still opens the app, on whatever screen it last showed.
	const route = isPlainOrganizationId(organizationId)
		? `${MOBILE_APP_OPEN_ROUTE}?organizationId=${organizationId}`
		: MOBILE_APP_OPEN_ROUTE;
	return (
		`intent://${route}#Intent;scheme=liveswitch;package=${MOBILE_ANDROID_PACKAGE_NAME};` +
		`S.browser_fallback_url=${encodeURIComponent(MOBILE_GOOGLE_PLAY_URL)};end`
	);
}

function isPlainOrganizationId(value: unknown): value is string {
	return typeof value === "string" && value.trim() !== "" && encodeURIComponent(value) === value;
}

/**
 * Mirror of contact-api isMobileAppBannerEligible: four or more dismissals means opted out and never
 * shows, a future snoozedUntil hides, anything else shows. No account at all (not loaded yet) hides.
 */
export function isMobileAppBannerEligibleAccount(
	account: Partial<MobileAppBannerAccount> | null | undefined,
	now: number = Date.now()
): boolean {
	if (!account) return false;
	if ((account.mobileAppBannerDismissCount ?? 0) >= MOBILE_APP_BANNER_OPT_OUT_DISMISSALS) return false;
	const until = account.mobileAppBannerSnoozedUntil ? new Date(account.mobileAppBannerSnoozedUntil).getTime() : NaN;
	return !Number.isFinite(until) || until <= now;
}

/**
 * Fold the API's answer (PUT /accounts/self/mobile-app-banner returns the account) back onto the live
 * account object both apps hold, so the rest of this page load sees the new snooze.
 */
export function applyMobileAppBannerServerFields(
	target: MobileAppBannerAccount | null | undefined,
	fields: Partial<MobileAppBannerAccount> | null | undefined
): void {
	if (!target || !fields) return;
	if ("mobileAppBannerDismissCount" in fields) target.mobileAppBannerDismissCount = fields.mobileAppBannerDismissCount ?? null;
	if ("mobileAppBannerSnoozedUntil" in fields) target.mobileAppBannerSnoozedUntil = fields.mobileAppBannerSnoozedUntil ?? null;
}

/**
 * Whether the current browser is one this banner is for: a phone or tablet browser tab, not an installed
 * Home Screen web app (that user already chose an installed experience) and not a desktop.
 */
export function isMobileAppBannerEligibleBrowser(targetWindow: Window | undefined): boolean {
	if (!targetWindow) return false;
	const platform = detectMobileAppPlatform(targetWindow.navigator?.userAgent, targetWindow.navigator);
	if (platform === "other") return false;
	try {
		return detectLaunchContext(targetWindow).launchSurface !== "home-screen";
	} catch {
		return true;
	}
}
